// src/contexts/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getHubTheme } from '../config/hubThemes';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');       // 'light' | 'dark'
  const [hubCode, setHubCode] = useState(null);       // e.g. 'sendp', 'client'
  const [hubTheme, setHubTheme] = useState(getHubTheme(null)); // resolved config object
  // The exact hubSettings entry App.js matched by URL at login (not just its
  // hubCode) — config.json can have multiple entries sharing a hubCode across
  // environments, so this is the one authoritative source for hub-specific
  // config flags like showUserInWorkflow.
  const [activeHub, setActiveHub] = useState(null);
  const prevHubRef = useRef(null);

  // -- Apply base theme class to <body> --
  useEffect(() => {
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(theme);
  }, [theme]);

  // -- Load base theme CSS --
  useEffect(() => {
    import(`../assets/css/${theme}.css`).catch((error) =>
      console.error(`Failed to load ${theme} theme CSS`, error)
    );
  }, [theme]);

  // -- Apply hub theme when hubCode changes --
  useEffect(() => {
    const resolved = getHubTheme(hubCode);
    setHubTheme(resolved);

    // Remove previous hub class
    if (prevHubRef.current) {
      document.body.classList.remove(`hub-${prevHubRef.current}`);
    }

    if (hubCode) {
      // Add hub body class for CSS scoping (body.hub-sendp, body.hub-client, etc.)
      document.body.classList.add(`hub-${hubCode}`);

      // Sync critical CSS custom properties from config to :root
      // This ensures inline styles and JS components pick up hub colours
      const root = document.documentElement;
      const { colors, fonts } = resolved;

      // Core colour tokens
      root.style.setProperty('--color-primary', colors.primary);
      root.style.setProperty('--primary-color', colors.primary); // legacy alias for Root.js
      if (colors.primaryDark)   root.style.setProperty('--color-primary-dark', colors.primaryDark);
      if (colors.accent)        root.style.setProperty('--color-accent', colors.accent);
      if (colors.secondary)     root.style.setProperty('--color-secondary', colors.secondary);
      if (colors.background)    root.style.setProperty('--color-background', colors.background);
      if (colors.text)          root.style.setProperty('--color-text', colors.text);
      if (colors.success)       root.style.setProperty('--color-success', colors.success);
      if (colors.warning)       root.style.setProperty('--color-warning', colors.warning);
      if (colors.danger)        root.style.setProperty('--color-danger', colors.danger);
      if (colors.highlight)     root.style.setProperty('--color-highlight', colors.highlight);

      // Font tokens
      if (fonts.base)    root.style.setProperty('--font-family-base', fonts.base);
      if (fonts.heading) root.style.setProperty('--font-family-heading', fonts.heading);

      // Lazy-load the hub-specific CSS overrides (sendp.css, client.css, etc.)
      // The CSS file provides the full token override set + component scoped rules
      import(`../assets/css/hubs/${hubCode}.css`).catch(() => {
        console.log(`No custom CSS for hub: ${hubCode} - using defaults`);
      });
    } else {
      // Reset to defaults when no hub is active
      const root = document.documentElement;
      const propsToReset = [
        '--color-primary', '--primary-color', '--color-primary-dark',
        '--color-accent', '--color-secondary', '--color-background',
        '--color-text', '--color-success', '--color-warning',
        '--color-danger', '--color-highlight',
        '--font-family-base', '--font-family-heading',
      ];
      propsToReset.forEach((prop) => root.style.removeProperty(prop));
    }

    prevHubRef.current = hubCode;
  }, [hubCode]);

  // -- Toggle light / dark --
  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  // -- Public API --
  const value = {
    theme,
    toggleTheme,
    hubCode,
    setHubCode,       // call after login / hub selection
    hubTheme,         // resolved config object for the active hub
    activeHub,        // the matched hubSettings entry from config.json (or null)
    setActiveHub,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
